import { apiCall } from '../../services/apiUtil';

/**
 * Ask the server for the scorer role on a game.
 *
 * ONE PLACE, because there are three callers — the squads Current Event tab,
 * the tournament Schedule tab, and the schedule builder — and they were each
 * deciding for themselves what a refusal meant. Two of them decided it meant
 * nothing, logged a warning and carried the user into the scoring screen as
 * though they held the role.
 *
 * `scorer_id` is one column on game_common and the server enforces it for every
 * sport, so the answer is the same whatever the game is. The caller gets a
 * plain verdict and decides what to show; it does not get to reinterpret a 409.
 *
 * @returns {Promise<{ claimed: boolean, scorer: object|null, scorerName: string|null }>}
 *   `claimed: false` means somebody else holds it — render read-only. It never
 *   throws for that case, because "you are a viewer" is an answer, not a fault.
 */
const refused = (scorer) => ({
    claimed: false,
    scorer: scorer ?? null,
    scorerName: (scorer
        ? `${scorer.first_name ?? ''} ${scorer.last_name ?? ''}`.trim()
        : '') || 'Someone',
});

export async function claimScorerRole(gameCommonId) {
    try {
        const res = await apiCall(`/games/${gameCommonId}/claim-scorer`, { method: 'POST' });

        // A REFUSAL ARRIVES AS A 200. The endpoint answers `{ claimed: false }`
        // with the holder attached rather than raising a 409 — the route has a
        // 409 branch, but the controller behind it returns instead of throwing.
        // Reading only the exception is how every caller before this one told a
        // viewer they were the scorer: the promise resolved, so they assumed yes.
        // Both shapes are read here so the answer stays right if the endpoint is
        // ever changed to throw.
        if (res && res.claimed === false) return refused(res.scorer);

        return { claimed: true, scorer: null, scorerName: null };
    } catch (err) {
        if (err.status === 409) return refused(err.data?.scorer);

        // Anything else is our problem, not a statement about who is scoring.
        // Claiming is best-effort — the server refuses a non-scorer on every
        // write anyway — so a caller who cannot reach it still proceeds and
        // finds out on the first point rather than being blocked at the door.
        console.error('[claimScorer] could not claim the scorer role:', err);
        return { claimed: true, scorer: null, scorerName: null };
    }
}
